/**
 * v3.21-1620 · Client Workspace Modal — reusable STANDARD operating window.
 *
 * Refactored from OnboardingCallModal (v3.19-1421).  Key differences:
 *   1. Opens with `clientId` alone — no `callSid` required.
 *   2. Title is "Client Workspace · <name>" (not "Onboarding Call").
 *   3. Section drawer includes Send Email · Call Assist · Callback ·
 *      Onboarding · Task · Appointment as first-class actions.
 *   4. Call context strip is optional (appears only when there is one).
 *   5. Notes autosave to the server-side draft store
 *      (POST /api/note-drafts) — not just localStorage.  Draft survives
 *      remount, hangup, refresh, PWA reload, remote hangup.
 *   6. Save Note uses canonical `/clients/{id}/add-note` (v3.21-1620
 *      fixed activity_type='note_added' + title) so exactly one
 *      canonical Activity row is created.
 *   7. Only the explicit Close button closes the workspace.  Every
 *      call-state transition (End Call, Complete Call, remote hangup,
 *      Twilio status=completed) leaves it open.
 *   8. Compose & Approve, Call Assist, Task, and Appointment are
 *      sibling modals — opening/closing them does not touch the
 *      workspace state.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import {
  ClipboardList, Send, MessageCircle, Loader2, CheckCircle2, User, Building2,
  Briefcase, HandCoins, DollarSign, Calendar, Mail, FileWarning, StickyNote,
  Sparkles, ListTodo, CalendarPlus, PhoneCall, PhoneMissed, X, BookOpen,
  ArrowRightCircle,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import ScheduleAppointmentModal from './ScheduleAppointmentModal';
import EmailComposeDialog from './EmailComposeDialog';
import CallAssistPanel from './CallAssistPanel';
import { useAuth } from '../contexts/AuthContext';
import { useClientWorkspace } from '../context/ClientWorkspaceContext';
// v3.21-1734 · Canonical Hub outbound telephony.  The Client Workspace
// CALL button MUST route every outbound through this shared hook — no
// separate telephony service, no bespoke phone-field priority chain.
// The hook enforces PBX entitlement, phone-system authorization,
// digit normalization, and the client-local-time preflight before
// hitting /api/telephony/call, and dispatches the workspace-open /
// active-call-pill events the rest of the Hub subscribes to.
import useOutboundCall from '../hooks/useOutboundCall';
import LateCallWarningModal from './LateCallWarningModal';
import useSystemVersion from '../hooks/useSystemVersion';   // v3.21-1795 · canonical version SSOT (shared with sidebar/footer)
import ClientWorkspaceSmsComposer from './ClientWorkspaceSmsComposer';   // v3.21-1794
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from './ui/dropdown-menu';

// -----------------------------------------------------------------------
// Session-scoped in-memory cache — Ghost chat + saved-note bump list.
// Persisted for the LIFE of the tab; server-authoritative data (drafts,
// notes, activities) is refetched, this only avoids Ghost transcript
// loss between quick re-opens of the same client.
// -----------------------------------------------------------------------
const SESSION_CACHE = new Map();
const sessionKey = (clientId) => `${clientId || 'nocli'}`;
function getSession(cid) {
  const k = sessionKey(cid);
  if (!SESSION_CACHE.has(k)) {
    SESSION_CACHE.set(k, { saved: [], ghost: [] });
  }
  return SESSION_CACHE.get(k);
}

const money = (v) =>
  (v == null || v === '') ? '—'
  : `$${Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

const relTime = (iso) => {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60)   return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s/60)}m ago`;
  if (s < 86400) return `${Math.floor(s/3600)}h ago`;
  return `${Math.floor(s/86400)}d ago`;
};

const isTouchDevice = () => {
  if (typeof window === 'undefined') return false;
  return ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
};

// -----------------------------------------------------------------------
// Quick Reference — read-only snapshot.
// -----------------------------------------------------------------------
const QRRow = ({ Icon, label, value, tone = 'slate' }) => (
  <div className="flex items-start gap-2 min-w-0" data-testid={`qr-${label.toLowerCase().replace(/\s+/g,'-')}`}>
    <Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 text-${tone}-500`} />
    <div className="min-w-0 flex-1">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold leading-tight">{label}</div>
      <div className="text-sm text-slate-900 font-medium truncate">{value ?? '—'}</div>
    </div>
  </div>
);

function QuickReference({ ctx, loading }) {
  if (loading) return (
    <div className="text-xs text-slate-500 flex items-center gap-2">
      <Loader2 className="h-3 w-3 animate-spin" /> Loading snapshot…
    </div>
  );
  if (!ctx) return <div className="text-xs text-slate-500">No context</div>;
  return (
    <div className="space-y-2" data-testid="client-workspace-quick-reference">
      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
        <QRRow Icon={User}          label="Client"     value={ctx.client_name} />
        <QRRow Icon={Building2}     label="Company"    value={ctx.company} />
        <QRRow Icon={ClipboardList} label="Stage"      value={ctx.deal_stage_label} />
        <QRRow Icon={Briefcase}     label="Rep"        value={ctx.sales_rep} />
        <QRRow Icon={User}          label="Manager"    value={ctx.manager} />
        <QRRow Icon={HandCoins}     label="Concierge"  value={ctx.application_concierge} />
        <QRRow Icon={DollarSign}    label="Requested"  value={money(ctx.loan_requested)} />
        <QRRow Icon={CheckCircle2}  label="Approved"   value={money(ctx.loan_approved)} tone="emerald" />
        <QRRow Icon={DollarSign}    label="Monthly"    value={money(ctx.monthly_payment)} />
        <QRRow Icon={Calendar}      label="Term"       value={ctx.loan_term ? `${ctx.loan_term} mo` : '—'} />
      </div>
      <div className="grid grid-cols-1 gap-2 pt-1 border-t border-slate-100">
        <div className="flex items-start gap-2">
          <Mail className="h-3.5 w-3.5 mt-0.5 shrink-0 text-slate-500" />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold leading-tight">Last Email</div>
            {ctx.last_email ? (
              <div className="text-xs text-slate-900" data-testid="qr-last-email">
                <div className="truncate font-medium">{ctx.last_email.subject}</div>
                <div className="text-slate-500 text-[11px] truncate">
                  {ctx.last_email.at_et} · {ctx.last_email.sender} · {relTime(ctx.last_email.at)}
                </div>
              </div>
            ) : <div className="text-xs text-slate-500">No emails on record</div>}
          </div>
        </div>
        <div className="flex items-start gap-2">
          <FileWarning className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${ctx.missing_documents?.length ? 'text-amber-600' : 'text-emerald-600'}`} />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold leading-tight">Missing Documents</div>
            {ctx.missing_documents?.length
              ? (
                <div className="flex flex-wrap gap-1 mt-0.5" data-testid="qr-missing-docs">
                  {ctx.missing_documents.map((d) => (
                    <span key={d} className="px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-800 text-[10px] font-semibold">{d}</span>
                  ))}
                </div>
              )
              : <div className="text-xs text-emerald-700 font-medium" data-testid="qr-no-missing-docs">None outstanding</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// Ghost Chat — free-form, context-aware.
// -----------------------------------------------------------------------
function GhostChat({ clientId, clientName, session, pushHistory }) {
  const [q, setQ] = useState('');
  const [asking, setAsking] = useState(false);
  const scrollRef = useRef(null);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [session.ghost.length, asking]);

  const ask = async (overrideText) => {
    const query = (overrideText != null ? overrideText : q).trim();
    if (!query || asking) return;
    setAsking(true);
    pushHistory({ role: 'user', text: query });
    if (!overrideText) setQ('');
    try {
      // v3.21-1630 — Client Workspace Ghost now calls the unified
      // Ghost service so approved-script retrieval, intent detection,
      // operational knowledge, and Suggested Wording labeling are
      // shared with Call Assist and Global Ghost.
      const res = await api.post('/ghost/answer', {
        query,
        capability:       'client_assistant',
        active_client_id: clientId,
      });
      const d = res.data || {};
      const body    = d.body || d.formatted_response || 'No answer available.';
      const source  = d.source || 'suggested_wording';
      const badge   = source === 'approved_script'       ? 'Approved Script'
                    : source === 'operational_knowledge' ? 'Approved Guidance'
                    : source === 'data'                  ? 'Hub Data'
                    : source === 'query_help'            ? 'Ghost Help'
                    :                                       'Suggested Wording — review before using';
      const text    = `[${badge}]\n${body}`;
      pushHistory({ role: 'ghost', text });
    } catch (e) {
      pushHistory({ role: 'ghost', text: `Ghost unavailable: ${e?.response?.data?.detail || e?.message || 'unknown error'}` });
    } finally {
      setAsking(false);
    }
  };
  const onKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); } };
  const prompts = useMemo(() => ([
    'What documents are still missing?',
    'What did we last email them?',
    'Any open follow-ups?',
    'Summarize recent notes',
  ]), []);

  return (
    <div className="space-y-2" data-testid="client-workspace-ghost-chat">
      <div
        className="text-[10.5px] text-indigo-900 bg-indigo-50 border border-indigo-100 rounded px-2 py-1 flex items-center gap-1.5 flex-wrap"
        data-testid="ghost-context-strip"
      >
        <Sparkles className="h-3 w-3 text-indigo-600 shrink-0" />
        <span>
          Ghost is live-connected to <span className="font-semibold">{clientName || 'this client'}</span>:{' '}
          <span className="text-indigo-700">deal · stage · timeline · emails · SMS · notes · documents · workflow · loan · team.</span>
        </span>
      </div>

      <div
        ref={scrollRef}
        className="rounded-md border border-slate-200 bg-white p-2 min-h-[10rem] max-h-[15rem] overflow-y-auto text-sm space-y-2"
        data-testid="ghost-chat-transcript"
      >
        {session.ghost.length === 0 && (
          <div className="text-[11px] text-slate-500 italic">
            Ask Ghost anything about this client.
          </div>
        )}
        {session.ghost.map((m, i) => (
          <div key={i} className={m.role === 'user' ? 'text-right' : 'text-left'}>
            <div className={
              m.role === 'user'
                ? 'inline-block bg-slate-900 text-white px-2 py-1 rounded-md text-xs max-w-[85%] whitespace-pre-wrap'
                : 'inline-block bg-indigo-50 border border-indigo-100 px-2 py-1 rounded-md text-xs max-w-[85%] whitespace-pre-wrap text-slate-900'
            }>
              {m.text}
            </div>
          </div>
        ))}
        {asking && <div className="text-xs text-slate-500 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Ghost is thinking…</div>}
      </div>

      <div className="flex items-center gap-1">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKey}
          placeholder="Ask Ghost about this client…"
          className="flex-1 text-sm rounded-md border border-slate-300 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-400"
          data-testid="ghost-chat-input"
        />
        <Button size="sm" onClick={() => ask()} disabled={!q.trim() || asking} data-testid="ghost-chat-send">
          {asking ? <Loader2 className="h-3 w-3 animate-spin" /> : <MessageCircle className="h-3 w-3" />}
        </Button>
      </div>
      <div className="flex flex-wrap gap-1" data-testid="ghost-suggestion-chips">
        <span className="text-[10px] text-slate-400 self-center pr-1">Ask:</span>
        {prompts.map((s) => (
          <button
            type="button"
            key={s}
            onClick={() => ask(s)}
            disabled={asking}
            className="text-[10px] px-1.5 py-0.5 rounded border border-slate-200 bg-white hover:bg-indigo-50 hover:border-indigo-300 text-slate-600 disabled:opacity-50"
            data-testid={`ghost-suggest-${s.slice(0, 10)}`}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// Notes — server-side draft autosave (v3.21-1620) + explicit Save Note.
// -----------------------------------------------------------------------
function WorkspaceNotes({ clientId, callSid, dealId, session, bumpSaved, onModalOpen }) {
  const [content, setContent]   = useState('');
  const [saving, setSaving]     = useState(false);
  const [noteColor, setNoteColor] = useState('neutral'); // v3.21-1667 attention color
  const [draftId, setDraftId]   = useState(null);
  const [draftHydrated, setDraftHydrated] = useState(false);
  const [draftStatus, setDraftStatus]     = useState('idle'); // idle | saving | saved | error
  const textareaRef = useRef(null);
  const hydrationKey = useRef('');

  // Server-side draft hydration (per client + call scope).
  useEffect(() => {
    if (!clientId) return;
    const key = `${clientId}::${callSid || 'nocall'}`;
    if (hydrationKey.current === key) return;
    hydrationKey.current = key;
    setDraftHydrated(false);
    let cancelled = false;
    (async () => {
      try {
        const params = { client_id: clientId };
        // Explicit empty string means "no-call scope".
        params.call_sid = callSid || '';
        const res = await api.get('/note-drafts', { params });
        if (cancelled) return;
        const drafts = res.data?.drafts || [];
        // Prefer exact call match; fall back to newest.
        const match = drafts.find((d) =>
          (d.call_sid || null) === (callSid || null)
        ) || drafts[0];
        if (match) {
          setContent(match.content || '');
          setDraftId(match.id);
        } else {
          setContent('');
          setDraftId(null);
        }
      } catch { /* ignore */ }
      finally {
        if (!cancelled) setDraftHydrated(true);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId, callSid]);

  // Debounced server-side autosave (~600ms) — v3.21-1620.
  useEffect(() => {
    if (!clientId || !draftHydrated) return undefined;
    // Also mirror to localStorage for offline recovery.
    try {
      const lsKey = `clientWorkspace.noteDraft:${clientId}:${callSid || 'nocall'}`;
      if (content) window.localStorage.setItem(lsKey, content);
      else         window.localStorage.removeItem(lsKey);
    } catch { /* ignore */ }
    const h = setTimeout(async () => {
      try {
        setDraftStatus('saving');
        const res = await api.post('/note-drafts', {
          client_id: clientId,
          call_sid:  callSid || null,
          deal_id:   dealId  || null,
          content,
        });
        setDraftId(res.data?.id || null);
        setDraftStatus('saved');
      } catch { setDraftStatus('error'); }
    }, 600);
    return () => clearTimeout(h);
  }, [content, clientId, callSid, dealId, draftHydrated]);

  useEffect(() => {
    if (!onModalOpen) return;
    if (isTouchDevice()) return;
    const t = window.setTimeout(() => { textareaRef.current?.focus(); }, 60);
    return () => window.clearTimeout(t);
  }, [onModalOpen]);

  const save = async () => {
    const text = (content || '').trim();
    if (!text) return;
    setSaving(true);
    try {
      const res = await api.post(`/clients/${clientId}/add-note`, {
        content:  text,
        call_sid: callSid || null,
        source:   'client_workspace',
        deal_id:  dealId || null,
        attention_color: noteColor,
      });
      bumpSaved({
        id:      res.data?.activity?.id || `local-${Date.now()}`,
        content: text,
        at:      new Date().toISOString(),
      });
      // Clear both server-side draft and local mirror.
      if (draftId) {
        try { await api.delete(`/note-drafts/${draftId}`); } catch { /* ignore */ }
        setDraftId(null);
      }
      try {
        const lsKey = `clientWorkspace.noteDraft:${clientId}:${callSid || 'nocall'}`;
        window.localStorage.removeItem(lsKey);
      } catch { /* ignore */ }
      setContent('');
      setNoteColor('neutral');
      setDraftStatus('idle');
      toast.success('Note saved to activity timeline');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save note');
    } finally {
      setSaving(false);
    }
  };
  const onKey = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') save();
  };

  const draftBadge = (() => {
    if (draftStatus === 'saving') return <span className="text-[10px] text-slate-500">Saving draft…</span>;
    if (draftStatus === 'saved')  return <span className="text-[10px] text-emerald-700" data-testid="draft-saved-badge">Draft saved</span>;
    if (draftStatus === 'error')  return <span className="text-[10px] text-red-600">Draft save error</span>;
    return null;
  })();

  return (
    <div className="space-y-2" data-testid="client-workspace-notes">
      <div className="rounded-md border-2 border-amber-300 bg-amber-50 p-2">
        <textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyDown={onKey}
          placeholder="Type notes here — server-side draft autosaves every ~600ms.  ⌘/Ctrl+Enter saves an authoritative Note to Activity."
          rows={5}
          className="w-full text-[13.5px] leading-snug rounded-sm border border-amber-400 bg-white px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none cursor-text placeholder:text-slate-500 placeholder:italic caret-amber-700 text-slate-900 font-medium"
          data-testid="client-workspace-note-input"
        />
        <div className="flex items-center justify-between gap-2 mt-1.5 flex-wrap">
          <div className="text-[11px] text-slate-700 truncate font-medium flex items-center gap-2">
            {callSid
              ? <>Attached to call <span className="font-mono">{String(callSid).slice(-8)}</span></>
              : 'No active call · saves as a standalone Note'}
            {draftBadge}
          </div>
          <Button
            onClick={save}
            disabled={!content.trim() || saving}
            className="h-10 px-4 bg-amber-600 hover:bg-amber-700 text-white font-semibold shadow-sm"
            data-testid="client-workspace-save-note"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}
            Save Note
          </Button>
        </div>
        {/* v3.21-1667 · Operator-chosen attention color (never inferred). */}
        <div className="flex items-center gap-1.5 mt-1.5" data-testid="note-attention-color-picker">
          <span className="text-[10px] text-slate-500 uppercase tracking-wider">Attention:</span>
          {[
            { v: 'neutral', label: 'Normal', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
            { v: 'green',   label: 'Green',  cls: 'bg-emerald-100 text-emerald-800 border-emerald-400' },
            { v: 'yellow',  label: 'Yellow', cls: 'bg-amber-100 text-amber-800 border-amber-400' },
            { v: 'red',     label: 'Red',    cls: 'bg-red-100 text-red-800 border-red-400' },
          ].map((c) => (
            <button
              key={c.v}
              type="button"
              onClick={() => setNoteColor(c.v)}
              className={`text-[10px] px-2 py-0.5 rounded border ${c.cls} ${noteColor === c.v ? 'ring-2 ring-offset-1 ring-slate-400 font-semibold' : 'opacity-70 hover:opacity-100'}`}
              data-testid={`note-color-${c.v}`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>
      {session.saved.length > 0 && (
        <div className="pt-1" data-testid="client-workspace-recent-notes">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1 flex items-center gap-1">
            <StickyNote className="h-3 w-3" /> Saved this session · {session.saved.length}
          </div>
          <div className="space-y-1 max-h-28 overflow-y-auto pr-1">
            {session.saved.map((r) => (
              <div key={r.id} className="text-xs text-slate-700 flex items-start gap-2">
                <CheckCircle2 className="h-3 w-3 mt-0.5 text-emerald-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="whitespace-pre-wrap break-words">{r.content}</div>
                  <div className="text-[10px] text-slate-500">{relTime(r.at)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------
// v3.21-1792 · WS1 · ClientCallPanel — Integrated calling surface.
//
// Compact call panel that sits inside the LEFT column of the Client
// Workspace directly under Onboarding Readiness.  Reuses:
//   • `useOutboundCall` — canonical outbound-call hook (SAME hook used
//     by DialPad, PhoneQR, Click-to-Call, and the Client Workspace
//     header Call button).  Enforces PBX entitlement, phone-system
//     authorisation, digit normalisation, and the client-local-time
//     preflight before dialling `/api/telephony/call`.
//   • QR generation matches PhoneQRModal exactly (same
//     api.qrserver.com endpoint) so ONE QR system exists across the
//     Hub — no duplicate QR implementation is introduced.
//   • `callable_numbers` array is produced server-side by
//     `services/onboarding_context.py` and is the single source of
//     truth for which phone numbers are dialable for this client.
//
// The Client Workspace header "Call" button already routes through
// `initiateOutboundCall` — it does NOT open the standalone
// PhoneClickable popup.  With this panel present, the header Call
// button also scrolls/focuses this panel via `callPanelRef` so
// operators see the QR + Copy affordances without leaving the
// workspace.
// -----------------------------------------------------------------------
const _cleanPhoneDigits = (phone) => (phone || '').replace(/\D/g, '');
const _formatPhone = (phone) => {
  const c = _cleanPhoneDigits(phone);
  if (c.length === 10) return `(${c.slice(0,3)}) ${c.slice(3,6)}-${c.slice(6)}`;
  if (c.length === 11 && c.startsWith('1')) return `+1 (${c.slice(1,4)}) ${c.slice(4,7)}-${c.slice(7)}`;
  return phone;
};
const _qrUrl = (phone) => {
  const c = _cleanPhoneDigits(phone);
  return `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(`tel:${c}`)}`;
};

function ClientCallPanel({ ctx, clientId, callable, outboundCalling, initiateOutboundCall }) {
  const [callAs, setCallAs] = useState('eqf_hub');
  const [copied, setCopied] = useState(false);
  const [pickedDigits, setPickedDigits] = useState('');
  const activeEntry = (callable && callable.length > 0)
    ? (callable.find((e) => e.digits === pickedDigits) || callable[0])
    : null;
  useEffect(() => {
    if (callable && callable.length > 0 && !pickedDigits) {
      setPickedDigits(callable[0].digits);
    }
  }, [callable, pickedDigits]);

  if (!callable || callable.length === 0) {
    return (
      <div data-testid="client-workspace-call-panel-empty">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1 flex items-center gap-1">
          <PhoneCall className="h-3 w-3" /> Call
        </div>
        <p className="text-[11px] text-slate-400 italic">No callable phone number on this client.</p>
      </div>
    );
  }

  const handleCall = () => {
    if (!activeEntry) return;
    return initiateOutboundCall(activeEntry.number, {
      clientId,
      clientName: activeEntry.owner || ctx?.client_name || undefined,
      callAs,
      context: 'client_workspace_integrated_panel',
    });
  };
  const handleCopy = async () => {
    if (!activeEntry) return;
    try {
      await navigator.clipboard.writeText(_cleanPhoneDigits(activeEntry.number));
      setCopied(true);
      toast.success('Copied');
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error('Failed to copy'); }
  };

  return (
    <div data-testid="client-workspace-call-panel">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5 flex items-center gap-1">
        <PhoneCall className="h-3 w-3" /> Call
      </div>
      <div className="rounded-md border border-slate-200 bg-white p-2.5 space-y-2">
        {callable.length > 1 && (
          <div>
            <label className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold block mb-0.5">Number</label>
            <select
              className="w-full h-8 text-xs border border-slate-300 rounded px-2 bg-white"
              value={pickedDigits}
              onChange={(e) => setPickedDigits(e.target.value)}
              data-testid="client-workspace-call-panel-number-select"
            >
              {callable.map((e) => (
                <option key={e.digits} value={e.digits}>
                  {e.label}{e.owner ? ` · ${e.owner}` : ''} — {e.number}
                </option>
              ))}
            </select>
          </div>
        )}
        {activeEntry && (
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Phone</div>
              <div className="text-sm font-semibold text-slate-900 truncate" data-testid="client-workspace-call-panel-phone">
                {_formatPhone(activeEntry.number)}
              </div>
            </div>
            <img
              src={_qrUrl(activeEntry.number)}
              alt={`QR for ${activeEntry.number}`}
              className="w-16 h-16 border border-slate-200 rounded shrink-0"
              data-testid="client-workspace-call-panel-qr"
            />
          </div>
        )}
        <div>
          <label className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold block mb-0.5">Call As</label>
          <select
            value={callAs}
            onChange={(e) => setCallAs(e.target.value)}
            className="w-full h-8 text-xs border border-slate-300 rounded px-2 bg-white"
            data-testid="client-workspace-call-panel-call-as"
          >
            {/* v3.21-1794 · Show originating outbound number next to
                each Call As label.  Labels are DISPLAY only — the
                underlying line-id value is unchanged so call routing
                is preserved.  Numbers must stay in sync with
                TWILIO_PHONE_NUMBER / TWILIO_PHONE_NUMBER_2. */}
            <option value="eqf_hub">EasyQuickFund / PTC Hub — (757) 448-3705</option>
            <option value="ptc_travel">Prestigious Travel Club — (407) 305-7738</option>
          </select>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            className="flex-1 h-8 bg-green-600 hover:bg-green-700 text-white text-xs"
            disabled={outboundCalling || !activeEntry}
            onClick={handleCall}
            data-testid="client-workspace-call-panel-call-now"
          >
            <PhoneCall className="h-3 w-3 mr-1" />
            {outboundCalling ? 'Calling...' : 'Call Now'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="flex-1 h-8 text-xs"
            onClick={handleCopy}
            data-testid="client-workspace-call-panel-copy"
          >
            {copied ? 'Copied!' : 'Copy'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function OnboardingReadinessChecklist({ clientId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null); // key currently saving

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get('/onboarding-readiness', { params: { client_id: clientId } });
        if (!cancelled) setItems(r.data?.items || []);
      } catch { if (!cancelled) setItems([]); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  const toggle = async (item) => {
    setSaving(item.item);
    const next = !item.checked;
    // Optimistic update
    setItems((prev) => prev.map((r) => r.item === item.item ? { ...r, checked: next } : r));
    try {
      const r = await api.post(`/onboarding-readiness/${clientId}/${item.item}`, { checked: next });
      setItems((prev) => prev.map((row) => row.item === item.item ? { ...row, ...r.data } : row));
    } catch (e) {
      // Rollback
      setItems((prev) => prev.map((r) => r.item === item.item ? { ...r, checked: !next } : r));
      toast.error(e?.response?.data?.detail || 'Failed to update readiness');
    } finally { setSaving(null); }
  };

  return (
    <div data-testid="onboarding-readiness">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1 flex items-center gap-1">
        <ArrowRightCircle className="h-3 w-3" /> Onboarding Readiness
      </div>
      {loading && <div className="text-xs text-slate-500">Loading…</div>}
      <div className="space-y-1">
        {items.map((row) => (
          <label key={row.item}
            className="flex items-start gap-2 text-[12px] text-slate-800 cursor-pointer group"
            data-testid={`readiness-${row.item}`}>
            <input
              type="checkbox"
              checked={!!row.checked}
              onChange={() => toggle(row)}
              disabled={saving === row.item}
              className="mt-0.5 h-3.5 w-3.5 accent-emerald-600"
              data-testid={`readiness-checkbox-${row.item}`}
            />
            <span className="flex-1">
              <span className={row.checked ? 'font-medium text-emerald-800' : ''}>{row.label}</span>
              {row.checked && row.checked_by_name && (
                <div className="text-[10px] text-slate-500">by {row.checked_by_name} · {relTime(row.checked_at)}</div>
              )}
            </span>
          </label>
        ))}
      </div>
      <div className="mt-2 p-2 rounded border border-amber-200 bg-amber-50 text-[10.5px] text-amber-900 leading-snug"
           data-testid="readiness-credential-safety">
        <b>Never</b> ask the client for their online-banking username or password.
        We only confirm they <b>have</b> their login available because the secure
        verification system may ask them to log into their bank directly.
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// Inline Task Creator — v3.21-1623 supports Assigned To + collaborators.
// -----------------------------------------------------------------------
function useInternalUserSearch() {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const term = (q || '').trim();
    if (term.length < 2) { setRows([]); return undefined; }
    const h = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get('/team-chats/users/search', { params: { q: term, limit: 12 } });
        setRows(r.data?.users || []);
      } catch { setRows([]); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(h);
  }, [q]);
  return { q, setQ, rows, loading };
}

function AssignPicker({ label, current, onPick, testIdPrefix, single = true, exclude = [] }) {
  const [open, setOpen] = useState(false);
  const { q, setQ, rows, loading } = useInternalUserSearch();
  const boxRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    window.addEventListener('mousedown', h);
    return () => window.removeEventListener('mousedown', h);
  }, [open]);
  const excludeSet = new Set(exclude);
  return (
    <div ref={boxRef} className="relative" data-testid={`${testIdPrefix}-wrap`}>
      <button type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-[11px] text-slate-800 border border-slate-300 rounded px-2 py-1 hover:bg-slate-100"
        data-testid={`${testIdPrefix}-open`}>
        {label}
      </button>
      {open && (
        <div className="absolute z-40 mt-1 w-64 rounded border border-slate-200 bg-white shadow-lg p-2"
             data-testid={`${testIdPrefix}-panel`}>
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search employees…"
                 className="w-full text-sm rounded border border-slate-300 px-2 py-1"
                 data-testid={`${testIdPrefix}-search`} />
          <div className="mt-1 max-h-56 overflow-y-auto">
            {loading && <div className="text-xs text-slate-500 py-1">searching…</div>}
            {!loading && q.trim().length >= 2 && rows.length === 0 &&
              <div className="text-xs text-slate-500 py-1">No employees found</div>}
            {rows.filter((u) => !excludeSet.has(u.id)).map((u) => (
              <button key={u.id} type="button"
                onClick={() => { onPick(u); if (single) setOpen(false); }}
                className="w-full text-left rounded px-2 py-1 hover:bg-slate-100"
                data-testid={`${testIdPrefix}-pick-${u.id}`}>
                <div className="text-sm font-medium truncate">{u.full_name || u.email}</div>
                <div className="text-[10px] text-slate-500 truncate">{u.email}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function InlineTaskForm({ open, onClose, clientId, clientName, onCreated }) {
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('');
  const [saving, setSaving] = useState(false);
  const [assignedTo, setAssignedTo]     = useState(null);   // {id, full_name}
  const [collaborators, setCollaborators] = useState([]);   // [{id, full_name}]
  useEffect(() => {
    if (open) {
      setTitle(''); setDescription(''); setDueDate(''); setDueTime('');
      setAssignedTo({ id: user?.id, full_name: user?.full_name || user?.email });
      setCollaborators([]);
    }
  }, [open, user?.id, user?.full_name, user?.email]);
  const submit = async (e) => {
    e?.preventDefault?.();
    if (!title.trim()) return;
    setSaving(true);
    try {
      const primaryId = assignedTo?.id || user?.id;
      const assigned_users = [primaryId, ...collaborators.map((c) => c.id)]
        .filter((v, i, a) => v && a.indexOf(v) === i);
      await api.post('/tasks', {
        title:          title.trim(),
        description:    description.trim() || null,
        client_id:      clientId,
        due_date:       dueDate || null,
        due_time:       dueTime || null,
        external_email: null,
        assigned_users,
      });
      toast.success('Task created');
      onCreated?.();
      onClose();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Failed to create task');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={!!open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="client-workspace-task-form">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <ListTodo className="h-4 w-4 text-emerald-700" /> Create Task
          </DialogTitle>
          <DialogDescription className="text-[11px] text-slate-500">
            Filed against {clientName || 'this client'} · assign to yourself or another team member.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-2">
          <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="Task title"
            className="w-full text-sm rounded-md border border-slate-300 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-slate-400"
            data-testid="task-form-title" />
          <textarea value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder="Description (optional)" rows={2}
            className="w-full text-sm rounded-md border border-slate-300 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-slate-400 resize-none"
            data-testid="task-form-description" />
          <div className="grid grid-cols-2 gap-2">
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="text-sm rounded-md border border-slate-300 px-2 py-1.5" data-testid="task-form-due-date" />
            <input type="time" value={dueTime} onChange={(e) => setDueTime(e.target.value)} className="text-sm rounded-md border border-slate-300 px-2 py-1.5" data-testid="task-form-due-time" />
          </div>
          <div className="flex flex-wrap items-center gap-2 text-[11px]">
            <span className="text-slate-500 font-medium">Assigned to:</span>
            <span className="font-semibold text-slate-800" data-testid="task-form-assignee">
              {assignedTo?.full_name || 'me'}
            </span>
            <AssignPicker label="Change" testIdPrefix="task-assignee"
              current={assignedTo}
              onPick={(u) => setAssignedTo({ id: u.id, full_name: u.full_name })} />
          </div>
          <div className="flex flex-wrap items-center gap-2 text-[11px]">
            <span className="text-slate-500 font-medium">Also include:</span>
            {collaborators.map((c) => (
              <span key={c.id} className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 flex items-center gap-1" data-testid={`task-collab-${c.id}`}>
                {c.full_name}
                <button type="button" onClick={() => setCollaborators((prev) => prev.filter((x) => x.id !== c.id))}
                        className="text-slate-500 hover:text-red-600">×</button>
              </span>
            ))}
            <AssignPicker label="+ Add" testIdPrefix="task-collab"
              current={null}
              exclude={[assignedTo?.id, ...collaborators.map((c) => c.id)].filter(Boolean)}
              onPick={(u) => setCollaborators((prev) => prev.some((x) => x.id === u.id) ? prev : [...prev, { id: u.id, full_name: u.full_name }])} />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" size="sm" disabled={!title.trim() || saving} data-testid="task-form-submit">
              {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <ListTodo className="h-3 w-3 mr-1" />}
              Create Task
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------
// Callback quick form — reuses existing POST /tasks with a due date.
// -----------------------------------------------------------------------
function InlineCallbackForm({ open, onClose, clientId, clientName, onCreated }) {
  const { user } = useAuth();
  const [when, setWhen] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [assignedTo, setAssignedTo] = useState(null);
  const [collaborators, setCollaborators] = useState([]);
  useEffect(() => {
    if (open) {
      const d = new Date(Date.now() + 24 * 3600 * 1000);
      setWhen(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`);
      setNote('');
      setAssignedTo({ id: user?.id, full_name: user?.full_name || user?.email });
      setCollaborators([]);
    }
  }, [open, user?.id, user?.full_name, user?.email]);
  const submit = async (e) => {
    e?.preventDefault?.();
    if (!when) return;
    setSaving(true);
    try {
      const [d, t] = when.split('T');
      const primaryId = assignedTo?.id || user?.id;
      const assigned_users = [primaryId, ...collaborators.map((c) => c.id)]
        .filter((v, i, a) => v && a.indexOf(v) === i);
      await api.post('/tasks', {
        title:          `Callback: ${clientName || 'client'}`,
        description:    note || null,
        client_id:      clientId,
        due_date:       d,
        due_time:       t || null,
        external_email: null,
        assigned_users,
      });
      toast.success(`Callback scheduled for ${assignedTo?.full_name || 'you'}`);
      onCreated?.();
      onClose();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Failed to schedule callback');
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="client-workspace-callback-form">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <PhoneCall className="h-4 w-4 text-sky-700" /> Schedule Callback
          </DialogTitle>
          <DialogDescription className="text-[11px] text-slate-500">
            Callback for {clientName || 'this client'} — assign to yourself or another team member.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-2">
          <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="w-full text-sm rounded-md border border-slate-300 px-2 py-1.5" data-testid="callback-form-when" />
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Optional note" className="w-full text-sm rounded-md border border-slate-300 px-2 py-1.5 resize-none" data-testid="callback-form-note" />
          <div className="flex flex-wrap items-center gap-2 text-[11px]">
            <span className="text-slate-500 font-medium">Assigned to:</span>
            <span className="font-semibold text-slate-800" data-testid="callback-form-assignee">
              {assignedTo?.full_name || 'me'}
            </span>
            <AssignPicker label="Change" testIdPrefix="callback-assignee"
              current={assignedTo}
              onPick={(u) => setAssignedTo({ id: u.id, full_name: u.full_name })} />
          </div>
          <div className="flex flex-wrap items-center gap-2 text-[11px]">
            <span className="text-slate-500 font-medium">Also include:</span>
            {collaborators.map((c) => (
              <span key={c.id} className="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 flex items-center gap-1" data-testid={`callback-collab-${c.id}`}>
                {c.full_name}
                <button type="button" onClick={() => setCollaborators((prev) => prev.filter((x) => x.id !== c.id))} className="text-slate-500 hover:text-red-600">×</button>
              </span>
            ))}
            <AssignPicker label="+ Add" testIdPrefix="callback-collab"
              current={null}
              exclude={[assignedTo?.id, ...collaborators.map((c) => c.id)].filter(Boolean)}
              onPick={(u) => setCollaborators((prev) => prev.some((x) => x.id === u.id) ? prev : [...prev, { id: u.id, full_name: u.full_name }])} />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" size="sm" disabled={!when || saving} data-testid="callback-form-submit">
              {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <PhoneCall className="h-3 w-3 mr-1" />}
              Schedule Callback
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------
// Call context strip — appears ONLY when a call is bound to the workspace.
// -----------------------------------------------------------------------
function CallContextStrip({ ctx, callSid, callState }) {
  if (!callSid) return null;
  const chip =
    callState === 'wrap_up'  ? { txt: 'Wrap-Up',  cls: 'bg-amber-100 text-amber-800 border-amber-300' } :
    callState === 'active'   ? { txt: 'Live Call', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' } :
    callState === 'completed'? { txt: 'Call Ended', cls: 'bg-slate-100 text-slate-700 border-slate-300' } :
                               { txt: 'Call',  cls: 'bg-slate-100 text-slate-700 border-slate-300' };
  return (
    <div
      className="text-[11px] flex items-center gap-2 rounded border px-2 py-1 bg-white flex-wrap"
      data-testid="client-workspace-call-strip"
    >
      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border uppercase tracking-wider ${chip.cls}`}
        data-testid="client-workspace-call-state-chip">
        {chip.txt}
      </span>
      <span className="text-slate-700">
        Call attached: <span className="font-mono">{String(callSid).slice(-8)}</span>
      </span>
      {ctx?.client_name && (
        <span className="text-slate-500">· {ctx.client_name}</span>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------
// Main Client Workspace modal shell.
// -----------------------------------------------------------------------
export default function ClientWorkspaceModal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    open, clientId, callSid, closeClientWorkspace,
  } = useClientWorkspace();

  const [ctx, setCtx]         = useState(null);
  const [loading, setLoading] = useState(false);
  const [openStamp, setOpenStamp] = useState(0);
  // Sibling action modals — all independent of workspace open state.
  const [taskOpen,     setTaskOpen]     = useState(false);
  const [apptOpen,     setApptOpen]     = useState(false);
  const [emailOpen,    setEmailOpen]    = useState(false);
  const [callAssistOpen, setCallAssistOpen] = useState(false);
  const [callbackOpen, setCallbackOpen] = useState(false);

  // Call state (best-effort, informational only).  Fetched when a
  // callSid is attached so the strip can render Wrap-Up vs Active.
  const [callState, setCallState] = useState(null);
  const [smsComposerOpen, setSmsComposerOpen] = useState(false);   // v3.21-1794
  // v3.21-1795 · Canonical runtime version SSOT — shared with the
  // sidebar / footer / SMS Dashboard version displays via the
  // `useSystemVersion` hook.  No hardcoded fallback.
  const clientWorkspaceVersion = useSystemVersion();

  // v3.21-1792 · WS1 — ref for the integrated call panel so the
  // workspace Call button can scroll/focus it without opening any
  // standalone call popup.
  const callPanelRef = useRef(null);
  const focusCallPanel = useCallback(() => {
    if (callPanelRef.current) {
      try {
        callPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        callPanelRef.current.classList.add('ring-2', 'ring-green-400');
        setTimeout(() => {
          if (callPanelRef.current) callPanelRef.current.classList.remove('ring-2', 'ring-green-400');
        }, 1500);
      } catch { /* ignore */ }
    }
  }, []);

  // v3.21-1795 · Listen for `phone-click:open-workspace` — dispatched
  // by PhoneClickable's disableModal branch whenever the operator
  // clicks a Client phone in DealDashboard / ClientDetail /
  // ClientQuickView / any other active Client workflow.  When the
  // workspace is already open for that Client we focus the CALL
  // panel immediately; when the workspace is opening for the first
  // time the ClientWorkspaceContext handler opens it, the workspace
  // mounts, and we retry the focus after a short delay so the
  // callable_numbers hydration + panel mount can complete.
  useEffect(() => {
    const onPhoneClick = (e) => {
      const { clientId: evtClientId, focus } = e.detail || {};
      if (focus !== 'call') return;
      if (evtClientId && evtClientId === clientId) {
        focusCallPanel();
      } else {
        setTimeout(() => focusCallPanel(), 350);
      }
    };
    window.addEventListener('phone-click:open-workspace', onPhoneClick);
    return () => window.removeEventListener('phone-click:open-workspace', onPhoneClick);
  }, [clientId, focusCallPanel]);

  const [, setBump] = useState(0);
  const session = getSession(clientId);
  const forceRerender = () => setBump((v) => v + 1);
  const bumpSaved   = useCallback((row) => { session.saved = [row, ...session.saved].slice(0, 25); forceRerender(); }, [session]);
  const pushHistory = useCallback((row) => { session.ghost = [...session.ghost, row]; forceRerender(); }, [session]);

  // v3.21-1734 · Canonical outbound-call hook (SSOT).  Shared with
  // DialPad, PhoneQR, Click-to-Call, and every other outbound surface.
  const {
    calling: outboundCalling,
    initiateOutboundCall,
    preflight: outboundPreflight,
    proceedLateCall,
    cancelLateCall,
    lateCallWarningOpen,
  } = useOutboundCall();

  // v3.21-1734 · Canonical multiple-number resolver.  The Client
  // Workspace does NOT pick a phone field itself — it consumes the
  // `callable_numbers` array produced by services/onboarding_context.py,
  // which is deduplicated and normalized server-side.  When zero
  // entries exist, the CALL button renders the existing no-callable
  // state.  When exactly one exists, the button dials that number
  // through `initiateOutboundCall`.  When two or more exist, the
  // button becomes the existing Hub number-selection experience
  // (dropdown of labeled entries: Client 1 · Mobile, Client 1 · Home,
  // Client 2 · Mobile, Client 2 · Home) — mirroring the per-number
  // PhoneClickable pattern used on the Deal Dashboard and Client
  // Detail page.  Every entry routes back through the same canonical
  // useOutboundCall hook.
  const callable = Array.isArray(ctx?.callable_numbers) ? ctx.callable_numbers : [];
  const startCanonicalCall = useCallback((entry) => {
    if (!entry || !entry.number) {
      toast.error('No callable phone number on this client.');
      return;
    }
    return initiateOutboundCall(entry.number, {
      clientId,
      clientName: entry.owner || ctx?.client_name || undefined,
      context: 'client_workspace',
    });
  }, [initiateOutboundCall, clientId, ctx?.client_name]);

  // Load onboarding-context whenever the workspace opens against a client.
  useEffect(() => {
    if (!open || !clientId) return;
    setOpenStamp((v) => v + 1);
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await api.get(`/clients/${clientId}/onboarding-context`);
        if (!cancelled) setCtx(res.data);
      } catch { if (!cancelled) setCtx(null); }
      finally    { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, clientId]);

  // Poll call state when a call is attached (light-weight; only when open).
  useEffect(() => {
    if (!open || !callSid) { setCallState(null); return undefined; }
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get(`/telephony/calls/${callSid}/status`);
        if (cancelled) return;
        const workflow = (r.data?.workflow_state || r.data?.status || '').toLowerCase();
        if (workflow === 'wrap_up')     setCallState('wrap_up');
        else if (workflow === 'completed' || workflow === 'ended' || workflow === 'canceled') setCallState('completed');
        else setCallState('active');
      } catch { /* ignore */ }
    };
    load();
    const h = setInterval(load, 5000);
    return () => { cancelled = true; clearInterval(h); };
  }, [open, callSid]);

  // v3.21-1620 — Only the explicit Close button closes the workspace.
  // Radix dialogs also close on Esc / outside-click by default.  We
  // intercept both to require deliberate closure.
  const onOpenChange = (v) => {
    if (v) return;
    closeClientWorkspace();
  };

  if (!open || !clientId) return null;
  const missingCount = ctx?.missing_documents?.length || 0;
  const dealId = ctx?.deal_id || null;

  return (
    <>
      <Dialog open onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-4xl p-0 gap-0 max-h-[92vh] overflow-y-auto"
          data-testid="client-workspace-modal"
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <DialogHeader className="px-4 pt-3 pb-2 border-b border-slate-200 sticky top-0 bg-white z-10">
            <DialogTitle className="flex items-center gap-2 text-base flex-wrap" data-testid="client-workspace-title">
              <ClipboardList className="h-4 w-4 text-emerald-700" />
              <span>Client Workspace</span>
              {ctx?.client_name && <span className="text-slate-500 font-normal">·</span>}
              {ctx?.client_name && <span className="font-semibold text-slate-900" data-testid="client-workspace-client-name">{ctx.client_name}</span>}
              {ctx?.deal_stage_label && (
                <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] font-semibold uppercase tracking-wider">
                  {ctx.deal_stage_label}
                </span>
              )}
              {missingCount > 0 && (
                <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-semibold" data-testid="header-missing-docs-chip">
                  {missingCount} missing doc{missingCount === 1 ? '' : 's'}
                </span>
              )}
              {session.saved.length > 0 && (
                <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[10px] font-semibold" data-testid="header-saved-notes-chip">
                  {session.saved.length} note{session.saved.length === 1 ? '' : 's'} saved
                </span>
              )}
              {callSid && callState === 'wrap_up' && (
                <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-semibold" data-testid="header-wrapup-chip">Wrap-Up</span>
              )}
              <div className="ml-auto flex items-center gap-1">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-8"
                  onClick={closeClientWorkspace}
                  data-testid="client-workspace-close"
                  title="Close Client Workspace (explicit)"
                >
                  <X className="h-3.5 w-3.5 mr-1" /> Close
                </Button>
              </div>
            </DialogTitle>
            <DialogDescription className="text-[11px] text-slate-500">
              One place to work this client — Quick Reference · Ghost · Notes · Email · Tasks · Appointments · Calls.
              Notes autosave server-side.  Only Close closes this window.
            </DialogDescription>

            {/* Optional call-context strip */}
            {callSid && (
              <div className="mt-2">
                <CallContextStrip ctx={ctx} callSid={callSid} callState={callState} />
              </div>
            )}

            {/* Primary action row */}
            <div className="flex gap-1.5 mt-2 flex-wrap" data-testid="client-workspace-actions">
              <Button
                type="button" size="sm" variant="outline"
                className="h-8 border-slate-300 bg-white hover:bg-slate-50"
                onClick={() => setEmailOpen(true)}
                data-testid="client-workspace-send-email-btn"
              >
                <Mail className="h-3.5 w-3.5 mr-1 text-slate-700" /> Send Email
              </Button>
              {/* v3.21-1734 · CALL — routes through the canonical Hub
                  outbound-call architecture (useOutboundCall + /api/
                  telephony/call).  Manual first-populated-phone
                  selection removed.  Multi-number selection now
                  mirrors the existing Deal Dashboard / Client Detail
                  per-number pattern via a labeled dropdown (Client 1 ·
                  Mobile, Client 1 · Home, Client 2 · Mobile, Client 2 ·
                  Home).  The Workspace stays open — useOutboundCall
                  dispatches `client-workspace:open` on success, which
                  the ClientWorkspaceContext no-ops when already open. */}
              {callable.length === 0 ? (
                <Button
                  type="button" size="sm" variant="outline" disabled
                  className="h-8 border-slate-300 bg-slate-50 text-slate-400 cursor-not-allowed"
                  data-testid="client-workspace-call-btn"
                  title="No callable phone number on this client."
                >
                  <PhoneCall className="h-3.5 w-3.5 mr-1" /> Call
                </Button>
              ) : callable.length === 1 ? (
                <Button
                  type="button" size="sm" variant="default"
                  className="h-8 bg-green-600 hover:bg-green-700 text-white"
                  disabled={outboundCalling}
                  data-testid="client-workspace-call-btn"
                  title={`Call ${callable[0].label}: ${callable[0].number}`}
                  onClick={() => { focusCallPanel(); startCanonicalCall(callable[0]); }}
                >
                  <PhoneCall className="h-3.5 w-3.5 mr-1" />
                  {outboundCalling ? 'Calling…' : 'Call'}
                </Button>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button" size="sm" variant="default"
                      className="h-8 bg-green-600 hover:bg-green-700 text-white"
                      disabled={outboundCalling}
                      data-testid="client-workspace-call-btn"
                      title="Multiple callable numbers — choose one"
                      onClick={focusCallPanel}
                    >
                      <PhoneCall className="h-3.5 w-3.5 mr-1" />
                      {outboundCalling ? 'Calling…' : `Call (${callable.length})`}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-64">
                    <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500">
                      Choose a number to call
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {callable.map((entry) => (
                      <DropdownMenuItem
                        key={entry.digits}
                        onSelect={() => startCanonicalCall(entry)}
                        data-testid={`client-workspace-call-option-${entry.digits}`}
                        className="cursor-pointer"
                      >
                        <PhoneCall className="h-3.5 w-3.5 mr-2 text-green-600" />
                        <div className="flex flex-col min-w-0">
                          <span className="text-xs font-medium text-slate-900 truncate">
                            {entry.label}
                          </span>
                          <span className="text-[11px] text-slate-500 truncate">
                            {entry.number}
                            {entry.owner ? ` · ${entry.owner}` : ''}
                          </span>
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <Button
                type="button" size="sm" variant="outline"
                className="h-8 border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800"
                onClick={() => setTaskOpen(true)}
                data-testid="client-workspace-create-task-btn"
              >
                <ListTodo className="h-3.5 w-3.5 mr-1" /> Create Task
              </Button>
              <Button
                type="button" size="sm" variant="outline"
                className="h-8 border-indigo-300 bg-indigo-50 hover:bg-indigo-100 text-indigo-800"
                onClick={() => setApptOpen(true)}
                data-testid="client-workspace-create-appt-btn"
              >
                <CalendarPlus className="h-3.5 w-3.5 mr-1" /> Create Appointment
              </Button>
              <Button
                type="button" size="sm" variant="outline"
                className="h-8 border-sky-300 bg-sky-50 hover:bg-sky-100 text-sky-800"
                onClick={() => setCallbackOpen(true)}
                data-testid="client-workspace-callback-btn"
              >
                <PhoneMissed className="h-3.5 w-3.5 mr-1" /> Callback
              </Button>
              <Button
                type="button" size="sm" variant="outline"
                className="h-8 border-purple-300 bg-purple-50 hover:bg-purple-100 text-purple-800"
                onClick={() => setCallAssistOpen(true)}
                data-testid="client-workspace-call-assist-btn"
              >
                <BookOpen className="h-3.5 w-3.5 mr-1" /> Call Assist
              </Button>
              {/* v3.21-1794 · Client Workspace SMS button opens an
                  IN-WORKSPACE SMS composer.  Previously (v1780) this
                  navigated to `/sms-dashboard?client_id=…` which is
                  the inbox/conversation surface — wrong destination
                  when the operator's intent is "send this Client a
                  text right now while I'm working the Client."  The
                  SMS Dashboard route remains available from the
                  sidebar (unchanged) for inbox / matched /
                  unassigned conversation management.  The composer
                  uses the canonical `/api/sms/send` backend + the
                  Deal Ownership SSOT to resolve recipients (Client 1,
                  Client 2, assigned team members from `client.assigned_*`
                  fields, and manual number).  Send routes through the
                  existing TextBee-configured sms_service — Preview
                  gating from v1779 preserved.  Post-send: composer
                  closes, Client Workspace remains open, timeline data
                  is refreshed via the existing WS pipeline. */}
              <Button
                type="button" size="sm" variant="outline"
                className="h-8 border-sky-300 bg-sky-50 hover:bg-sky-100 text-sky-800"
                data-testid="client-workspace-sms-btn"
                title="Send an SMS to this Client or Deal team member"
                onClick={() => setSmsComposerOpen(true)}
              >
                <MessageCircle className="h-3.5 w-3.5 mr-1" /> SMS
              </Button>
              <div className="ml-auto text-[10px] text-slate-500 self-center" data-testid="client-workspace-version-tag">
                {/* v3.21-1795 · Canonical runtime version SSOT.
                    Reads via `useSystemVersion()` — the SAME shared
                    hook that powers the sidebar / footer / SMS
                    Dashboard version tag.  No hardcoded fallback;
                    the hook returns "" when the version fetch
                    fails, so a broken build cannot silently surface
                    a stale literal here.  Every future version
                    bump in backend/main.py + server.py +
                    frontend/public/version.json propagates through
                    this display automatically. */}
                {clientWorkspaceVersion}
              </div>
            </div>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3">
            <div
              className="order-1 md:order-1 md:row-span-2 border border-slate-200 rounded-md p-2.5 bg-white"
              data-testid="client-workspace-section-quick-ref"
            >
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5 flex items-center gap-1">
                <ClipboardList className="h-3 w-3" /> Quick Reference
              </div>
              <QuickReference ctx={ctx} loading={loading} />

              {/* v3.21-1623 · Onboarding Readiness Checklist —
                  persisted server-side per (client, item).  Stores
                  ONLY readiness booleans + audit metadata; never
                  bank credentials of any kind. */}
              <div className="mt-3 pt-2 border-t border-slate-100" data-testid="client-workspace-onboarding-entry">
                <OnboardingReadinessChecklist clientId={clientId} />
              </div>

              {/* v3.21-1792 · WS1 · Integrated Calling Panel.
                  Reuses the canonical outbound-call SSOT
                  (`useOutboundCall`) that powers the Client Workspace
                  header Call button, DialPad, PhoneQR, and every
                  click-to-call surface in the Hub.  QR is generated
                  from the same `api.qrserver.com` service used by
                  PhoneQRModal so there is ONE QR system across the
                  Hub, not two.  Placement: LEFT column, directly
                  under Onboarding Readiness. */}
              <div
                ref={callPanelRef}
                id="client-workspace-call-panel"
                className="mt-3 pt-2 border-t border-slate-100"
                data-testid="client-workspace-integrated-call-panel"
              >
                <ClientCallPanel
                  ctx={ctx}
                  clientId={clientId}
                  callable={callable}
                  outboundCalling={outboundCalling}
                  initiateOutboundCall={initiateOutboundCall}
                />
              </div>
            </div>

            <div
              className="order-2 md:order-2 border border-indigo-200 rounded-md p-2.5 bg-white"
              data-testid="client-workspace-section-ghost"
            >
              <div className="text-[10px] uppercase tracking-wider text-indigo-700 font-semibold mb-1.5 flex items-center gap-1">
                <Sparkles className="h-3 w-3 text-indigo-500" /> Ghost Assistant
              </div>
              <GhostChat
                clientId={clientId}
                clientName={ctx?.client_name}
                session={session}
                pushHistory={pushHistory}
              />
            </div>

            <div
              className="order-3 md:order-3 border-2 border-amber-200 rounded-md p-2.5 bg-white"
              data-testid="client-workspace-section-notes"
            >
              <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold mb-1.5 flex items-center gap-1">
                <StickyNote className="h-3 w-3" /> Notes
              </div>
              <WorkspaceNotes
                clientId={clientId}
                callSid={callSid}
                dealId={dealId}
                session={session}
                bumpSaved={bumpSaved}
                onModalOpen={openStamp}
              />
            </div>
          </div>
          {/* v3.21-1638 · Block 4 — Call Assist is rendered as a
              SIBLING of the Workspace DialogContent so it lives OUTSIDE
              the Workspace's Radix FocusScope + backdrop.  This
              guarantees the two windows are independently focusable and
              neither traps or blocks the other.  CallAssistPanel itself
              uses createPortal-to-body + fixed positioning; making it a
              sibling here only affects the focus/event tree. */}
        </DialogContent>
      </Dialog>

      <CallAssistPanel
        open={callAssistOpen}
        onClose={() => setCallAssistOpen(false)}
        clientId={clientId}
      />

      {/* Sibling action modals — none of these touch workspace state. */}
      <InlineTaskForm
        open={taskOpen}
        onClose={() => setTaskOpen(false)}
        clientId={clientId}
        clientName={ctx?.client_name}
      />
      <InlineCallbackForm
        open={callbackOpen}
        onClose={() => setCallbackOpen(false)}
        clientId={clientId}
        clientName={ctx?.client_name}
      />
      {clientId && (
        <ScheduleAppointmentModal
          open={apptOpen}
          onOpenChange={setApptOpen}
          client={{ id: clientId, full_name: ctx?.client_name || '' }}
          currentUser={user}
          onScheduled={() => { toast.success('Appointment booked'); setApptOpen(false); }}
        />
      )}
      <EmailComposeDialog
        open={emailOpen}
        onOpenChange={setEmailOpen}
        clientId={clientId}
        clientEmail={ctx?.client_email}
        clientName={ctx?.client_name}
      />

      {/* v3.21-1734 · Canonical late-call preflight warning.  Renders
          when useOutboundCall's client-local-time preflight determines
          the call would land outside legal calling hours.  The user
          can Cancel (no call placed) or Proceed Anyway (audit logged,
          then dial).  Identical to DialPad / PhoneQR / Click-to-Call. */}
      <LateCallWarningModal
        open={lateCallWarningOpen}
        preflight={outboundPreflight}
        onCancel={cancelLateCall}
        onProceed={proceedLateCall}
        proceeding={outboundCalling}
      />
      {/* v3.21-1794 · Client Workspace in-workspace SMS composer.
          Opens over the workspace when the SMS button is clicked.
          Never navigates away; Client context stays loaded. */}
      <ClientWorkspaceSmsComposer
        open={smsComposerOpen}
        onClose={() => setSmsComposerOpen(false)}
        client={ctx?.client}
        ctx={ctx}
      />
    </>
  );
}
